import i18n from '@/i18n'
import store from '@/state/store'
import { hasLineOrderFeatureAccess } from '@/helpers/tms-line-order'
import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Product from '@/services/rest-api/lib/route-optimization/product'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import { GET_CITIES, GET_PROVINCES  } from '@/services/api/master/address'


const getFeatureFlagValue = store.getters['identity/getFeatureFlagValue']
const hasLineOrderAccess = hasLineOrderFeatureAccess()

const formatListPreview = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return '-'
  if (list.length <= 2) return list.join(', ')
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`
}

const getLineOrderData =
  () =>
  async (opts = {}) => {
    const filters = Array.isArray(opts?.filters) ? [...opts.filters] : []

    const response = await Delivery.getLineOrder({
      ...opts,
      filters,
    })
  const paginatedResult = response?.data?.paginated_result || []

  return {
    metadata: {
      ...(response?.metadata || {}),
    },
    data: {
      ...(response?.data || {}),
      paginated_result: paginatedResult.map((item) => ({
        ...item,
        id: item.line_order,
        line_order_label: item.line_order,
        delivery_order_count: item.delivery_order_count,
        shipped_customers: item.customer_names,
        created_by: item.created_bys,
      })),
    },
    isPagination: true,
  }
  }

let provincesCache = null
let provincesPromise = null
let citiesCache = null
let citiesPromise = null

async function fetchProvinces(opts) {
  // If we don't have the full data cached, fetch it
  if (!provincesCache) {
    // If there's already a request in flight, wait for it
    if (provincesPromise) {
      await provincesPromise
    } else {
      // Fetch all provinces without search to cache the full dataset
      provincesPromise = this.$apollo.query({
        query: GET_PROVINCES,
        fetchPolicy: 'cache-first',
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999
            },
          },
        },
      })

      const res = await provincesPromise
      const data = res?.data?.data?.provinces || []
      provincesCache = data?.data?.map((item) => ({
        id: item.name,
        name: item.name,
      })) || []
      provincesPromise = null
    }
  }

  // Filter cached data based on search term
  const searchTerm = opts?.search?.toLowerCase() || ''
  let filteredProvinces = provincesCache
  if (searchTerm) {
    filteredProvinces = provincesCache.filter(item => 
      item.name.toLowerCase().includes(searchTerm)
    )
  }

  return {
    metadata: {
      total_count: filteredProvinces.length,
      page: 1,
    },
    data: {
      paginated_result: filteredProvinces,
      ids: filteredProvinces.map((item) => item.id),
    },
  }
}

async function fetchCities(opts) {
  // If we don't have the full data cached, fetch it
  if (!citiesCache) {
    // If there's already a request in flight, wait for it
    if (citiesPromise) {
      await citiesPromise
    } else {
      // Fetch all cities without search to cache the full dataset
      citiesPromise = this.$apollo.query({
        query: GET_CITIES,
        fetchPolicy: 'cache-first',
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999
            },
          },
        },
      })

      const res = await citiesPromise
      const data = res?.data?.data?.cities || []
      citiesCache = data?.data?.map((item) => ({
        id: item.name,
        name: item.name,
      })) || []
      citiesPromise = null
    }
  }

  // Filter cached data based on search term
  const searchTerm = opts?.search?.toLowerCase() || ''
  let filteredCities = citiesCache
  if (searchTerm) {
    filteredCities = citiesCache.filter(item => 
      item.name.toLowerCase().includes(searchTerm)
    )
  }

  return {
    metadata: {
      total_count: filteredCities.length,
      page: 1,
    },
    data: {
      paginated_result: filteredCities,
      ids: filteredCities.map((item) => item.id),
    },
  }
}

export const getTableFilterFields = () => [
  {
    name: 'shipped_customer_id',
    isShow: true,
    label: i18n.t('routeOptimization.fields.shippedCustomerName'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.shippedCustomerName'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: i18n.t('global.object.customerName'),
        key: 'name',
        isSortable: true,
      },
    ],
  },

  {
    name: 'line_order',
    isShow: hasLineOrderAccess,
    label: 'Line Order',
    type: 'value-help-v2',
    placeholder: 'Pilih Line Order',
    trackBy: 'id',
    displayedBy: 'line_order_label',
    listGetter: getLineOrderData(),
    multiple: true,
    visibleTagCount: 2,
    canSelectAll: true,
    tableFields: [
      {
        label: 'Line Order',
        key: 'line_order',
        isSortable: true,
      },
      {
        label: 'Jumlah Pengiriman',
        key: 'delivery_order_count',
        isSortable: true,
      },
      {
        label: 'Pelanggan Penerima',
        key: 'shipped_customers',
        isSortable: false,
        formatValue: (value) => formatListPreview(value),
      },
      {
        label: 'Dibuat Oleh',
        key: 'created_by',
        isSortable: false,
        formatValue: (value) => formatListPreview(value),
      },
    ],
  },

  {
    name: 'created_on',
    isShow: true,
    label: i18n.t('routeOptimization.fields.createdOn'),
    type: 'date-picker',
    datepickerType: 'datetime',
    valueType: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    format: 'DD MMM YYYY HH:mm',
    titleFormat: 'DD MMM YYYY',
    rangeSeparator: ' - ',
    range: true,
    confirm: true,
    confirmText: i18n.t('global.action.apply'),
    disabledDate: (date) => {
      return date > new Date()
    },
    shortcuts: [
      {
        text: i18n.t('global.time.today'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: i18n.t('global.time.yesterday'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 1)
          startDate.setHours(0, 0, 0)

          endDate.setDate(endDate.getDate() - 1)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: i18n.t('global.time.last3Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 3)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: i18n.t('global.time.last7Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 7)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
    ],
  },

  {
    name: 'expected_delivered_on',
    isShow: true,
    label: i18n.t('routeOptimization.fields.expectedDeliveredOn'),
    type: 'date-picker',
    datepickerType: 'datetime',
    valueType: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    format: 'DD MMM YYYY HH:mm',
    titleFormat: 'DD MMM YYYY',
    rangeSeparator: ' - ',
    range: true,
    confirm: true,
    confirmText: i18n.t('global.action.apply'),
  },

  {
    name: 'destination_address_id',
    isShow: true,
    label: i18n.t('routeOptimization.fields.destinationAddress'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.destinationAddress'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Address.get(),
    multiple: true,
    tableFields: [
      {
        label: i18n.t('routeOptimization.fields.locationName'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
      {
        label: i18n.t('global.object.city'),
        key: 'city',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },

  {
    name: 'destination_address_category',
    isShow: true,
    label: i18n.t('routeOptimization.fields.addressCategory'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.addressCategory'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Address.getAddressCategory(),
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: i18n.t('routeOptimization.fields.addressCategory'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },
  {
    name: 'destination_address_province',
    isShow: true,
    label: i18n.t('routeOptimization.fields.destinationProvince'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.destinationProvince'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: fetchProvinces,
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: i18n.t('global.object.province'),
        key: 'name',
        isSortable: true,
      },
    ],
  },
  {
    name: 'destination_address_city',
    isShow: true,
    label: i18n.t('routeOptimization.fields.destinationCity'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.destinationCity'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: fetchCities,
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: i18n.t('master.form.address.regencyCity'),
        key: 'name',
        isSortable: true,
      },
    ],
  },
  {
    name: 'product_category',
    isShow: true,
    label: i18n.t('routeOptimization.fields.productCategory'),
    type: 'value-help-v2',
    placeholder: `${i18n.t('global.form.select')} ${i18n.t(
      'routeOptimization.fields.productCategory'
    )}`,
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Product.getProductCategory(),
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: i18n.t('routeOptimization.fields.productCategory'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },

  {
    type: 'range-input',
    isShow: true,
    label: i18n.t('routeOptimization.fields.totalWeight'),
    min: {
      name: 'load_weight_min',
      label: i18n.t('global.field.minimum'),
      type: 'number',
      validation: 'number|min:0,value|minLessThanMax:load_weight_min,load_weight_max',
      'validation-messages': {
        minLessThanMax: `${i18n.t('global.field.minimum')} ${i18n.t(
          'global.validation.max'
        )} ${i18n.t('global.field.maximum')}`,
      },
      suffix: 'Kg',
      'validation-behavior': 'live',
      'error-behavior': 'live',
    },
    max: {
      name: 'load_weight_max',
      label: i18n.t('global.field.maximum'),
      type: 'number',
      validation: 'number|min:0,value|minLessThanMax:load_weight_min,load_weight_max',
      'validation-messages': {
        minLessThanMax: `${i18n.t('global.field.maximum')} ${i18n.t(
          'global.validation.min'
        )} ${i18n.t('global.field.minimum')}`,
      },
      suffix: 'Kg',
      'validation-behavior': 'live',
      'error-behavior': 'live',
    },
  },
  {
    type: 'range-input',
    isShow: true,
    label: 'Total Volume',
    min: {
      name: 'load_volume_min',
      label: i18n.t('global.field.minimum'),
      type: 'number',
      validation: 'number|min:0,value|minLessThanMax:load_volume_min,load_volume_max',
      'validation-messages': {
        minLessThanMax: `${i18n.t('global.field.minimum')} ${i18n.t(
          'global.validation.max'
        )} ${i18n.t('global.field.maximum')}`,
      },
      suffix: 'Cbm',
      'validation-behavior': 'live',
      'error-behavior': 'live',
    },
    max: {
      name: 'load_volume_max',
      label: i18n.t('global.field.maximum'),
      type: 'number',
      validation: 'number|min:0,value|minLessThanMax:load_volume_min,load_volume_max',
      'validation-messages': {
        minLessThanMax: `${i18n.t('global.field.maximum')} ${i18n.t(
          'global.validation.min'
        )} ${i18n.t('global.field.minimum')}`,
      },
      suffix: 'Cbm',
      'validation-behavior': 'live',
      'error-behavior': 'live',
    },
  },
]

export const tableFilterFields = getTableFilterFields()
